import type { MarketingLocale } from "./marketing-copy";

export type CookiePolicySection = {
  title: string;
  paragraphs?: readonly string[];
  bullets?: readonly string[];
  subsections?: ReadonlyArray<{
    title: string;
    paragraphs: readonly string[];
  }>;
};

export type CookieRecord = {
  name: string;
  category: string;
  provider: string;
  purpose: string;
  retention: string;
};

export const cookiePolicyCopy = {
  en: {
    title: "Cookie Management Policy",
    version: "Version 1.1 — September 2026",
    precedence: "In the event of any discrepancy or difference in interpretation between the French and English versions of this Cookie Management Policy, the French version shall prevail.",
    backHome: "Back to home",
    sections: [
      {
        title: "1. Introduction",
        paragraphs: [
          'This Cookie Management Policy explains how 12 services-conseils inc. (the operator of the CVBuilder.ca platform) uses cookies and similar technologies in our artificial intelligence resume generation application (hereinafter, the "Service").',
          "This policy aims to inform you transparently about the cookies placed, their purposes, and how you can manage your preferences, in accordance with Quebec's Law 25 and Canada's PIPEDA.",
        ],
      },
      {
        title: "2. Our Philosophy: Minimization and Privacy",
        paragraphs: [
          "Unlike many online services, we do not use any advertising cookies, marketing tracking pixels, or third-party tracking tools for commercial profiling.",
          "The cookies we use are strictly served to:",
        ],
        bullets: [
          "Ensure the technical operation and security of the application.",
          "Store your session preferences (e.g., language selection).",
          "Manage the persistence of your resume creation session (in accordance with the 7-calendar-day retention rule or the immediate deletion option).",
          "Secure financial transactions for paying users.",
        ],
      },
      {
        title: "3. Categories of Cookies Used on the Platform",
        subsections: [
          {
            title: "A. Strictly Necessary Cookies (Mandatory)",
            paragraphs: [
              "These cookies are essential for the basic functioning of the platform. They cannot be disabled in our systems because the application cannot function properly without them.",
              "Security & CSRF: Protection against malicious attacks and form injections.",
              "Session Management & Multi-Tenancy: Maintenance of your secure connection and identification of the client organization (in white-label B2B usage).",
              "Language Preferences: Remembering the selected language (French, English, or Bilingual) at Step 1 of the workflow.",
              "Access and Quota Validation: Verifies your account status upon login (free or paid) and links to our database to count the number of attempts associated with your email address, unlocking or restricting access to the AI features.",
            ],
          },
          {
            title: "B. Functionality and Retention Cookies (Candidate Session)",
            paragraphs: [
              "Workflow Persistence (7 Days): Retains your profile and latest uploaded document for up to 7 calendar days if you return to the platform to edit your resume.",
              'Immediate Purge Option: If you check the "Delete my data immediately upon leaving the platform" box at Step 4, these session cookies are destroyed as soon as your browser is closed.',
            ],
          },
          {
            title: "C. Secure Payment Cookies (PCI-DSS Certified Third Parties)",
            paragraphs: [
              "When completing an individual payment (B2C), our third-party payment processors (such as Stripe or Helcim) may set temporary technical cookies to prevent fraud and secure transaction processing. No sensitive banking details are recorded on our own servers.",
            ],
          },
        ],
      },
      {
        title: "5. Managing and Withdrawing Consent (Quebec Law 25)",
        paragraphs: [
          "In accordance with the requirements of Law 25, non-essential cookies require your explicit consent.",
          "Because our platform uses no advertising or non-essential third-party tracking cookies, using our Service sets only the functional and security cookies strictly necessary to deliver the requested service.",
          "You can configure your browser to block or remove cookies at any time:",
        ],
      },
    ] satisfies readonly CookiePolicySection[],
    table: {
      title: "4. Summary Table of Cookies",
      headers: ["Cookie Name / Key", "Category", "Provider", "Purpose / Function", "Retention Period"],
      records: [
        { name: "cvb_session_id", category: "Essential", provider: "First-party", purpose: "Maintains active user session through the 4-step workflow.", retention: "Session / Max 7 calendar days" },
        { name: "cvb_lang", category: "Essential", provider: "First-party", purpose: "Stores chosen language preference (FR / EN).", retention: "30 days" },
        { name: "cvb_tenant_id", category: "Essential", provider: "First-party", purpose: "Identifies the white-label partner or organization (B2B).", retention: "Session" },
        { name: "XSRF-TOKEN", category: "Security", provider: "First-party", purpose: "Prevents Cross-Site Request Forgery (CSRF) attacks.", retention: "Session" },
        { name: "__stripe_mid / __stripe_sid", category: "Payment", provider: "Stripe (Third-party)", purpose: "Fraud detection and prevention during credit card processing.", retention: "Session to 1 year (processor dependent)" },
      ] satisfies readonly CookieRecord[],
    },
    browserInstructions: [
      "Google Chrome: Settings → Privacy and security → Cookies and other site data.",
      "Mozilla Firefox: Options → Privacy & Security → Cookies and Site Data.",
      "Safari: Preferences → Privacy → Block all cookies.",
    ],
    browserNote: "Note: Fully blocking essential cookies in your browser may prevent the resume generation workflow from functioning correctly at Step 1 and Step 4.",
    contact: {
      title: "6. Contact Information",
      intro: "For any questions regarding our Cookie Management Policy or to exercise your privacy rights:",
      details: [
        "Data Protection Officer: Patrick Lacroix",
        "Organization: 12 services-conseils inc. (12sc)",
        "Email: team@cvbuilder.ca / info@12sc.ca",
        "Website: 12sc.ca",
      ],
    },
  },
  fr: {
    title: "Politique de gestion des témoins (cookies)",
    version: "Version 1.1 — Septembre 2026",
    precedence: "En cas de divergence ou de différence d'interprétation entre les versions française et anglaise de la présente Politique de gestion des témoins, la version française prévaudra.",
    backHome: "Retour à l’accueil",
    sections: [
      {
        title: "1. Introduction",
        paragraphs: [
          "La présente Politique de gestion des témoins explique comment 12 services-conseils inc. (exploitant de la plateforme CVBuilder.ca) utilise des témoins de navigation (ci-après « cookies ») et des technologies similaires sur notre application de génération de CV par intelligence artificielle (ci-après le « Service »).",
          "Cette politique vise à vous informer en toute transparence de la nature des témoins déposés, de leur utilité et de la façon dont vous pouvez gérer vos préférences, conformément à la Loi 25 au Québec et à la LPRPDE au Canada.",
        ],
      },
      {
        title: "2. Notre philosophie : Minimisation et confidentialité",
        paragraphs: [
          "Contrairement à de nombreux services en ligne, nous n'utilisons aucun témoin publicitaire, aucun marqueur de ciblage marketing ni aucun outil de traçage tiers à des fins de profilage commercial.",
          "Les témoins que nous utilisons servent exclusivement à :",
        ],
        bullets: [
          "Assurer le fonctionnement technique et la sécurité de l'application.",
          "Conserver vos préférences de session (ex. choix de la langue).",
          "Gérer la persistance de votre session de création de CV (conformément à la règle des 7 jours civils ou à l'option de suppression immédiate).",
          "Sécuriser les transactions financières pour les utilisateurs payants.",
        ],
      },
      {
        title: "3. Catégories de témoins utilisés sur la plateforme",
        subsections: [
          {
            title: "A. Témoins strictement nécessaires (Obligatoires)",
            paragraphs: [
              "Ces témoins sont indispensables au fonctionnement de base de la plateforme. Ils ne peuvent pas être désactivés dans nos systèmes car l'application ne peut pas fonctionner correctement sans eux.",
              "Sécurité & CSRF : Protection contre les attaques malveillantes et les injections de formulaires.",
              "Gestion de session & Multi-tenant : Maintien de votre connexion sécurisée et identification de l'organisation cliente (en cas d'utilisation sous licence B2B en marque blanche).",
              "Préférences de langue : Mémorisation de la langue sélectionnée (Français, Anglais) à l'étape 1 du parcours.",
              "Validation des accès et quotas : Vérification de l'état de votre compte lors de la connexion (statut gratuit ou payant) et lien avec la base de données pour comptabiliser le nombre d'essais associés à votre courriel afin de débloquer ou restreindre l'accès à l'IA.",
            ],
          },
          {
            title: "B. Témoins de fonctionnalité et de rétention (Session candidat)",
            paragraphs: [
              "Persistance du parcours (7 jours) : Permet de conserver votre profil et le dernier document téléversé pendant un maximum de 7 jours civils si vous revenez sur la plateforme pour modifier votre CV.",
              "Option de purge immédiate : Si vous cochez la case « Supprimer mes données immédiatement en quittant la plateforme » à l'étape 4, ces témoins de session sont détruits dès la fermeture de votre navigateur.",
            ],
          },
          {
            title: "C. Témoins de paiement sécurisé (Tiers certifiés PCI-DSS)",
            paragraphs: [
              "Lorsque vous effectuez un paiement individuel (B2C), nos prestataires de paiement tiers (tels que Stripe ou Helcim) peuvent déposer des témoins techniques temporaires pour prévenir la fraude bancaire et sécuriser le traitement de la transaction. Aucune donnée bancaire sensible n'est enregistrée sur nos propres serveurs.",
            ],
          },
        ],
      },
      {
        title: "5. Gestion et retrait du consentement (Loi 25 du Québec)",
        paragraphs: [
          "Conformément aux exigences de la Loi 25, les témoins non strictement nécessaires requièrent votre consentement explicite.",
          "Comme notre plateforme n'utilise aucun témoin publicitaire ou de traçage tiers non essentiel, le simple fait d'utiliser notre Service dépose uniquement les témoins fonctionnels et de sécurité nécessaires à la prestation du service demandé.",
          "Vous pouvez à tout moment configurer votre navigateur web pour bloquer ou supprimer les témoins :",
        ],
      },
    ] satisfies readonly CookiePolicySection[],
    table: {
      title: "4. Tableau récapitulatif des témoins",
      headers: ["Nom du témoin / Clé", "Catégorie", "Identité", "Finalité / Rôle", "Durée de conservation"],
      records: [
        { name: "cvb_session_id", category: "Essentiel", provider: "Interne", purpose: "Maintient la session active de l'utilisateur à travers le parcours en 4 étapes.", retention: "Session / Max 7 jours civils" },
        { name: "cvb_lang", category: "Essentiel", provider: "Interne", purpose: "Mémorise la préférence linguistique choisie (FR / EN).", retention: "30 jours" },
        { name: "cvb_tenant_id", category: "Essentiel", provider: "Interne", purpose: "Identifie l'organisation ou le portail en marque blanche (B2B).", retention: "Session" },
        { name: "XSRF-TOKEN", category: "Sécurité", provider: "Interne", purpose: "Prévient les attaques de type Cross-Site Request Forgery (CSRF).", retention: "Session" },
        { name: "__stripe_mid / __stripe_sid", category: "Paiement", provider: "Stripe (Tiers)", purpose: "Détection et prévention de la fraude lors de la transaction bancaire.", retention: "Session à 1 an (selon le processeur)" },
      ] satisfies readonly CookieRecord[],
    },
    browserInstructions: [
      "Google Chrome : Paramètres → Confidentialité et sécurité → Cookies et autres données de site.",
      "Mozilla Firefox : Options → Vie privée et sécurité → Cookies et données de sites.",
      "Safari : Préférences → Confidentialité → Bloquer tous les cookies.",
    ],
    browserNote: "Note : Le blocage complet des témoins essentiels dans votre navigateur peut empêcher le fonctionnement du parcours de génération de CV à l'étape 1 et à l'étape 4.",
    contact: {
      title: "6. Contact",
      intro: "Pour toute question concernant notre Politique de gestion des témoins ou l'exercice de vos droits en matière de vie privée :",
      details: [
        "Responsable de la protection des données : Patrick Lacroix",
        "Organisation : 12 services-conseils inc. (12sc)",
        "Courriel : team@cvbuilder.ca / info@12sc.ca",
        "Site web : 12sc.ca",
      ],
    },
  },
} as const satisfies Record<MarketingLocale, object>;
